import {
    Button,
    Flex,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeaderCell,
    TableRow,
    Text,
    Title,
} from '@tremor/react'
import { ArrowPathIcon, EyeIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import { useApiV1BillingInvoicesList } from '../../api/api.gen'
import { apiHostname } from '../../api/ApiConfig'
import { useAuth } from '../../utilities/auth'

export default function BillingPage() {
    const { getAccessTokenSilently } = useAuth()
    const downloadPDF = async (id: string) => {
        try {
            const token = await getAccessTokenSilently()
            const response = await fetch(
                `${apiHostname()}/api/v1/billing/invoices/${id}/pdf`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            )

            if (!response.ok) {
                throw new Error('Network response was not ok')
            }

            // Convert the response into a Blob
            const blob = await response.blob()

            // Create a URL for the Blob
            const url = window.URL.createObjectURL(blob)

            // Create a temporary <a> element to trigger download
            const a = document.createElement('a')
            a.href = url
            a.download = 'bill.pdf' // Specify the downloaded file name
            document.body.appendChild(a)
            a.click()

            // Clean up
            a.remove()
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Error downloading PDF:', error)
        }
    }

    const {
        response: invoiceList,
        isLoading: invoiceListLoading,
        isExecuted: invoiceListExecuted,
        sendNow: refresh,
    } = useApiV1BillingInvoicesList()

    return (
        <Flex
            className="p-4 bg-gray-50 w-auto min-w-[1000px] max-w-fit mt-4 rounded-md"
            flexDirection="col"
            justifyContent="start"
            alignItems="start"
        >
            <Flex flexDirection="row" justifyContent="between">
                <Title className="text-teal-600">Invoices</Title>
                <Flex className="w-fit space-x-2">
                    <Button
                        icon={ArrowPathIcon}
                        variant="secondary"
                        color="teal"
                        disabled={invoiceListExecuted && invoiceListLoading}
                        onClick={() => refresh()}
                    />
                </Flex>
            </Flex>
            <Table className="w-full">
                <TableHead>
                    <TableRow>
                        <TableHeaderCell>ID</TableHeaderCell>
                        <TableHeaderCell>Date</TableHeaderCell>
                        <TableHeaderCell>Amount</TableHeaderCell>
                        <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {invoiceList?.invoices?.map((v) => (
                        <TableRow>
                            <TableCell>{10000 + (v.id || 0)}</TableCell>
                            <TableCell>
                                {dayjs(v.date).format('MMM D, YYYY')}
                            </TableCell>
                            <TableCell>{v.amount?.toFixed(3)} OMR</TableCell>
                            <TableCell>
                                <EyeIcon
                                    onClick={() => {
                                        downloadPDF(String(v.id || 0))
                                    }}
                                    className="w-4 h-4 text-teal-500 cursor-pointer"
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                    {(invoiceList?.invoices?.length || 0) === 0 && (
                        <TableRow>
                            <TableCell colSpan={6}>
                                <Text className="w-full text-gray-400 text-center font-normal">
                                    {invoiceListExecuted && invoiceListLoading
                                        ? 'Loading'
                                        : 'Empty list'}
                                </Text>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Flex>
    )
}
